import { z } from "zod";

// Define the address schema
const addressSchema = z.object({
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  country: z.string(),
  latLng: z.string().optional(),
  webUrl: z.string().optional(),
  localityLabel: z.string().optional(),
});

// Define the camp experience schema
const campExperienceSchema = z.object({
  programName: z.string().min(1, "Program name is required"),
  topActivities: z.string().optional(),
  outdoorTimeScale: z.number().min(0).max(100),
  structuredTimeScale: z.number().min(0).max(100),
  physicalityScale: z.number().min(0).max(100),
  themeMainKey: z.string().optional(),
  themeOtherKeys: z.array(z.string()).optional(),
  hideScales: z.boolean().optional(),
});

// Per-camp hub overrides (KB-4297). Shape only: the KB Admin tab validates the keys against the live
// campHub set (`validateCampHubOverrides`) and is the field's only writer — the editor's save and
// publish strip it (`omitServerOwnedCampLocationFields`), so a stale form copy can never revert it.
export const campHubOverridesSchema = z
  .object({
    include: z.array(z.string()),
    exclude: z.array(z.string()),
    note: z.string().nullable().optional(),
  })
  .refine((v) => !v.include.some((k) => v.exclude.includes(k)), {
    message: "A hub can't be both included and excluded",
  });

// Define the main form schema
export const formSchema = z.object({
  name: z.string().min(1, "Location name is required"),
  visibility: z.enum(["v_published", "v_unpublished", "v_draft", "v_archived", "v_kbOnly"]).optional(),
  urlMain: z.string().optional(),
  searchResultExcerpt: z.string().optional(),
  descLocation: z.string().optional(),
  descTimingAndBooking: z.string().optional(),
  address: addressSchema,
  doesWaitlist: z.boolean().nullable(),
  doesScholarships: z.boolean().nullable(),
  scholarshipNotes: z.string().optional(),
  
  // Fields for the About Your Camp tab
  religiousAffiliationKeys: z.array(z.string()).optional(),
  religiousAffiliationNotes: z.string().optional(),
  allergyKeys: z.array(z.string()).optional(),
  allergyNotes: z.string().optional(),
  supportForKeys: z.array(z.string()).optional(),
  supportForNotes: z.string().optional(),
  campExperiences: z.array(campExperienceSchema),
  
  // Adding pricing fields
  regularOfferIncrementKey: z.string().optional(),
  incrementPrice: z.number().nullable().optional(),
  priceNote: z.string().optional(),
  singleDayOption: z.boolean().optional(),
  singleDayPrice: z.number().nullable().optional(),
  
  // Adding the changeDescription field
  changeDescription: z.string().optional(),

  campHubOverrides: campHubOverridesSchema.optional(),
});

// Export the types
export type LocationFormData = z.infer<typeof formSchema>;
export type AddressFormData = z.infer<typeof addressSchema>;
export type CampExperienceFormData = z.infer<typeof campExperienceSchema>;
